import { useMemo, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { tenantConfig } from '../config/tenant'
import Nav from '../components/Nav'
import Seo from '../components/Seo'
import Footer from '../components/Footer'
import LeadCaptureModal from '../components/LeadCaptureModal'
import LenderLogo from '../components/LenderLogo'
import ComparisonRateDisclaimer from '../components/ComparisonRateDisclaimer'
import RangeField from '../components/RangeField'
import { useRates } from '../hooks/useRates'
import { monthlyRepayment } from '../lib/repayment'
import { formatNumberWithCommas, stripToNumber } from '../utils/formatCurrency'

/**
 * "How much could you save?" — a refinance savings page under /tools. The visitor
 * enters their loan balance, current rate and term; we compare that against the
 * lenders on our live panel and list the ones that BEAT their rate, each with its
 * monthly repayment, plus the headline saving. Then we capture the enquiry as a
 * lead. Shows each lender's comparison rate + the verbatim disclaimer (NCCP, same
 * rule as the rate board). No em-dashes in any copy.
 */

const AUD = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 })

export default function SavingsPage() {
  const [balance, setBalance] = useState<number>(500_000)
  const [balanceText, setBalanceText] = useState<string>('500,000')
  const [currentRate, setCurrentRate] = useState<number>(6.5)
  const [term, setTerm] = useState<number>(30)
  const [leadOpen, setLeadOpen] = useState(false)

  const { data } = useRates()

  function onBalance(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = stripToNumber(e.target.value).replace(/^0+(?=\d)/, '')
    setBalanceText(raw ? formatNumberWithCommas(raw) : '')
    setBalance(Number(raw) || 0)
  }
  useEffect(() => {
    setBalanceText(balance ? formatNumberWithCommas(String(balance)) : '')
  }, [balance])

  const current = useMemo(() => monthlyRepayment(balance, currentRate, term, 'PI'), [balance, currentRate, term])

  // Lenders on our panel that beat the entered rate, each with its repayment + saving.
  const beating = useMemo(() => {
    const panel = data?.major ?? []
    return panel
      .map((r) => {
        const ratePct = Number((r.rate * 100).toFixed(2))
        return {
          lender: r.lender,
          lender_name: r.lender_name,
          product_name: r.product_name,
          ratePct,
          compPct: r.comparison_rate != null ? Number((r.comparison_rate * 100).toFixed(2)) : null,
          repayment: monthlyRepayment(balance, ratePct, term, 'PI'),
        }
      })
      .filter((r) => r.ratePct < currentRate && r.repayment > 0)
      .sort((a, b) => a.ratePct - b.ratePct)
  }, [data, balance, currentRate, term])

  const best = beating[0] ?? null
  const monthlySaving = best ? Math.max(0, current - best.repayment) : 0
  const yearlySaving = monthlySaving * 12
  const totalSaving = monthlySaving * term * 12
  const hasSaving = monthlySaving > 0

  const chip = 'text-xs font-medium px-3 py-1 rounded-full bg-[#F4F4F0] text-gs-text-soft'

  return (
    <>
      <Seo
        title="How much could you save?"
        path="/tools/savings"
        description="See how much you could save by refinancing. We compare your current home loan against the lenders on our panel and show the ones that beat your rate. Indicative only."
      />
      <Nav />
      <main id="main" className="pt-36 pb-20">
        <section className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-gs-text-muted mb-2">Refinance</div>
            <h1 className="font-heading text-3xl md:text-5xl text-ink font-semibold mb-4">How much could you save?</h1>
            <p className="text-lg text-gs-text-soft max-w-2xl mx-auto">
              Loyalty rarely pays on a home loan. Enter your current loan and we will show you the lenders
              on our panel that beat your rate, and what switching could save you.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-6 text-sm text-gs-text-soft">
              <span className="inline-flex items-center gap-1.5"><Tick /> Instant comparison</span>
              <span className="inline-flex items-center gap-1.5"><Tick /> No credit check needed</span>
              <span className="inline-flex items-center gap-1.5"><Tick /> {tenantConfig.lenderCount}+ lenders</span>
            </div>
          </div>

          {/* Inputs */}
          <div className="bg-white border border-[#E8E8E2] rounded-2xl p-6 grid gap-5 md:grid-cols-3 md:items-end">
            <div>
              <label htmlFor="sv-balance" className="block text-sm font-medium mb-1">Loan balance</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 flex items-center text-sm text-gs-text-muted">$</span>
                <input id="sv-balance" type="text" inputMode="numeric" value={balanceText} onChange={onBalance}
                  placeholder="0" className="w-full py-2.5 pl-7 pr-3 border border-[#D8D8D0] rounded-lg text-sm tab-num" />
              </div>
            </div>
            <div>
              <label htmlFor="sv-rate" className="block text-sm font-medium mb-1">Your current rate</label>
              <div className="relative">
                <input id="sv-rate" type="number" step={0.01} value={Number.isFinite(currentRate) ? currentRate : ''}
                  onChange={(e) => setCurrentRate(parseFloat(e.target.value) || 0)}
                  className="w-full py-2.5 pl-3 pr-8 border border-[#D8D8D0] rounded-lg text-sm tab-num" />
                <span className="absolute inset-y-0 right-3 flex items-center text-sm text-gs-text-muted">%</span>
              </div>
            </div>
            <RangeField id="sv-term" label="Years remaining" value={term} onChange={setTerm}
              min={1} max={30} step={1} format="number" minLabel="1" maxLabel="30" />
          </div>

          {/* Results */}
          <div className="mt-8">
            {hasSaving && best ? (
              <>
                <div className="text-center">
                  <div className="text-gs-text-soft">Based on your loan, you could be paying</div>
                  <div className="font-heading font-bold leading-none my-2" style={{ color: tenantConfig.primaryColor }}>
                    <span className="text-5xl md:text-6xl tab-num">{AUD.format(Math.round(monthlySaving))}</span>
                    <span className="text-2xl text-gs-text-soft"> / mo less</span>
                  </div>
                  <div className="text-gs-text-soft">than you are paying right now.</div>
                  <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-sm font-semibold"
                    style={{ background: '#F4FBD9', color: tenantConfig.primaryColor }}>
                    {AUD.format(Math.round(yearlySaving))}/yr · {AUD.format(Math.round(totalSaving))} over {term} years
                  </div>
                  <div className="flex flex-wrap justify-center gap-2 mt-5">
                    <span className={chip}>{AUD.format(balance)} balance</span>
                    <span className={chip}>Principal &amp; interest</span>
                    <span className={chip}>{term} year term</span>
                    <span className={chip}>No credit check needed</span>
                  </div>
                </div>

                {/* Current rate */}
                <div className="mt-8 rounded-2xl border p-5 flex items-center justify-between"
                  style={{ borderColor: '#F0D8A8', background: '#FFF9EE' }}>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#B7791F' }}>Your current rate</div>
                    <div className="font-heading text-ink">Your home loan</div>
                  </div>
                  <div className="font-heading text-3xl font-bold tab-num" style={{ color: '#B7791F' }}>{currentRate.toFixed(2)}%</div>
                </div>

                {/* Lenders that beat your rate */}
                <div className="mt-6">
                  <div className="flex items-baseline justify-between mb-2">
                    <h2 className="font-heading text-lg text-ink font-semibold">Lenders that beat your rate</h2>
                    <span className="text-sm text-gs-text-muted">{beating.length} matched</span>
                  </div>
                  <ul className="divide-y divide-[#E8E8E2] border-t border-[#E8E8E2]">
                    {beating.map((r) => (
                      <li key={`${r.lender}-${r.product_name}`} className="py-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <LenderLogo name={r.lender_name} size={28} />
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-ink truncate">{r.lender_name}</div>
                            <div className="text-xs text-gs-text-muted truncate">{r.product_name}</div>
                          </div>
                        </div>
                        <div className="text-right shrink-0 leading-tight">
                          <div className="font-heading font-semibold text-ink tab-num">{r.ratePct.toFixed(2)}%</div>
                          <div className="text-[11px] text-gs-text-muted tab-num">
                            {AUD.format(Math.round(r.repayment))}/mo{r.compPct != null ? ` · ${r.compPct.toFixed(2)}% comp.` : ''}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <button onClick={() => setLeadOpen(true)}
                  className="mt-8 w-full px-6 py-4 rounded-xl font-semibold"
                  style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}>
                  Get my savings report
                </button>
                <p className="text-center text-xs text-gs-text-muted mt-3">No obligation. A broker confirms your real numbers and the right fit.</p>
              </>
            ) : (
              <div className="rounded-2xl p-8 text-center text-white" style={{ background: tenantConfig.primaryColor }}>
                <div className="font-heading text-2xl font-semibold mb-2">You are on a sharp rate already.</div>
                <p className="text-white/75 max-w-lg mx-auto">
                  Based on what you entered, none of the lenders on our panel currently beat your rate. Rates move
                  often though, so it is still worth a quick review with a broker.
                </p>
                <button onClick={() => setLeadOpen(true)} className="mt-6 px-6 py-3.5 rounded-xl font-semibold text-sm"
                  style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}>
                  Talk to a broker
                </button>
              </div>
            )}
          </div>

          <ComparisonRateDisclaimer />

          <p className="text-[11px] text-gs-text-muted leading-relaxed mt-4 max-w-3xl">
            Indicative only, principal and interest, based on the figures you enter and the advertised rates on our
            panel. It is not a quote or an offer of credit. Your actual savings depend on your full financial situation,
            fees and lender assessment. A broker confirms the real numbers with you.{' '}
            <Link to="/tools" className="underline underline-offset-2 hover:text-ink">See the full calculator</Link>.
          </p>
        </section>
      </main>
      <Footer />

      <LeadCaptureModal
        open={leadOpen}
        onClose={() => setLeadOpen(false)}
        source="calculator"
        heading="Get your savings report"
        blurb="Enter your details and a broker will prepare your personalised refinance savings review."
        submitLabel="Send me my savings report"
        context={{
          enquiry_type: 'Refinance savings',
          loan_amount: balance,
          interest_rate: currentRate,
          term_years: term,
          monthly_repayment: Math.round(current),
        }}
      />
    </>
  )
}

function Tick() {
  return (
    <span className="w-4 h-4 rounded-full grid place-items-center shrink-0" style={{ background: tenantConfig.accentColor }}>
      <svg viewBox="0 0 20 20" className="w-3 h-3 text-ink" fill="currentColor" aria-hidden="true">
        <path d="M16.7 5.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-3-3a1 1 0 111.4-1.4L9 11.6l6.3-6.3a1 1 0 011.4 0z" />
      </svg>
    </span>
  )
}
